import React from 'react';
import { Grid, Typography } from '@mui/material';
import { SparkChipSelection, SparkDialog } from 'sharedComponents/UI';
import SubscriptionCard from './SubscriptionCard';
import SubscriptionCardSkeleton from '../../../../sharedComponents/Skeleton/SubscriptionCardSkeleton';
import { useSubscriptionBillingIntervals } from 'hooks/EntityTypes/useSubscriptionBillingIntervals';
import { useSubscriptionTypes } from 'hooks/EntityTypes/useSubscriptioonType';
import { useResize } from 'hooks/common';
import { useSubscriptionCreditOptions } from 'hooks/EntityTypes/useSubscriptionCreditOptions';
import { useStripeCheckoutSession } from 'hooks/Stripe/useStripeCheckoutSession';
import type { SubscriptionBillingInterval } from 'types/entityTypes/SubscriptionBillingInterval';

type SubscriptionModalProps = {
  subscriptionTypeID: string | undefined;
  close: () => void;
  accountID: string | undefined;
  open: boolean;
  subscriptionCreditOptionsID: string | undefined;
};
const MONTH = 'Month';

export type PriceOverCredits = { id: string; credits: number; priceID: string };

function SubscriptionModal({
  subscriptionTypeID,
  accountID,
  open,
  close,
  subscriptionCreditOptionsID,
}: SubscriptionModalProps) {
  const {
    options,
    subscriptionBillingIntervals,
    intervalNameMap,
    isLoading: isSubscriptionBillingIntervalLoading,
  } = useSubscriptionBillingIntervals();

  const {
    mutateAsync: addCheckoutSession,
    isLoading: isAddingCheckoutSession,
  } = useStripeCheckoutSession();

  const { subscriptionTypes, isLoading: isSubscriptionTypesLoading } =
    useSubscriptionTypes();
  const {
    groupedBySubscriptionBillingIntervalID,
    isLoading: isSubscriptionCreditOptionsLoading,
  } = useSubscriptionCreditOptions();

  const [subscriptionBillingInterval, setSubscriptionBillingInterval] =
    React.useState<SubscriptionBillingInterval | undefined>();
  const [priceOverCredits, setPriceOverCredit] = React.useState<
    PriceOverCredits[]
  >([]);

  React.useEffect(() => {
    if (intervalNameMap?.[MONTH]?.id && !subscriptionBillingInterval)
      setSubscriptionBillingInterval(intervalNameMap?.[MONTH]);
  }, [subscriptionBillingInterval, intervalNameMap]);

  const { isMobile } = useResize();
  const currentSelectedCreditOption =
    groupedBySubscriptionBillingIntervalID?.[
      subscriptionBillingInterval?.id as string
    ];

  const onSliceChange = (credits: number, typeID: string, priceID: string) => {
    setPriceOverCredit((pre) => {
      const index = pre.findIndex((item) => item.id === typeID);
      if (index !== -1) {
        return pre.map((item) =>
          item.id === typeID
            ? { ...item, credits: credits as number, priceID: priceID }
            : item
        );
      } else {
        return [
          ...pre,
          { id: typeID, credits: credits as number, priceID: priceID },
        ];
      }
    });
  };

  const isLoading =
    isSubscriptionBillingIntervalLoading ||
    isSubscriptionCreditOptionsLoading ||
    isSubscriptionTypesLoading;
  const getSubscription = (
    subscriptionCreditOptionID: string,
    priceID: string
  ) => {
    const checkoutAttributes = {
      accountID: accountID,
      id: subscriptionCreditOptionID,
      priceID: priceID,
    };
    addCheckoutSession(checkoutAttributes);
  };

  return (
    <SparkDialog
      open={open}
      onClose={close}
      title=''
      hideTitle={true}
      sx={{ '& .MuiPaper-root': { borderRadius: 0 } }}
      fullScreen={true}
    >
      <Grid
        container
        padding={2}
        paddingX={5}
        justifyContent='center'
        alignItems='center'
        direction='column'
        gap={3}
      >
        <Grid
          item
          display={'flex'}
          flexDirection='column'
          gap={2}
          alignItems={'center'}
          pt={2}
        >
          <Typography variant='h3' fontFamily='Mont-Semibold' color='#404040'>
            Spark your Growth
          </Typography>
          <Typography
            variant='h5'
            fontFamily='Mont-Semibold'
            sx={{
              background: 'linear-gradient(95deg,  #E45282, #DD529C, #6268AF)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Whether you are a solo innovator or a growing enterprise , our
            <br></br>
            straightforward pricing puts the power of Sparkup within reach.
          </Typography>
        </Grid>
        <Grid item container>
          <Grid item display='flex' justifyContent='center' width='100%'>
            <SparkChipSelection
              options={[...(options || [])]?.reverse() || []}
              defaultOption={subscriptionBillingInterval?.id}
              onSelect={(option) => {
                const interval = subscriptionBillingIntervals?.find(
                  (item) => item.id === option.value
                );
                setSubscriptionBillingInterval(interval);
              }}
              containerStyles={{
                padding: '2px',
                border: '2px solid #eee',
                width: 'max-content',
                height: 'max-content',
              }}
              selectionBackgroundColor='#F58320'
              unSelectionFontColor='#888e99'
            />
          </Grid>
          <Grid
            item
            container
            direction='row'
            justifyContent='space-between'
            marginTop={11}
            rowGap={2}
          >
            {[...(subscriptionTypes || [])].reverse()?.map((type, index) => {
              if (isLoading) return <SubscriptionCardSkeleton />;
              return (
                <SubscriptionCard
                  key={type.id}
                  type={type}
                  onSliceChange={onSliceChange}
                  currentSelectedCreditOption={currentSelectedCreditOption}
                  priceOverCredits={priceOverCredits}
                  groupedBySubscriptionBillingIntervalID={
                    groupedBySubscriptionBillingIntervalID
                  }
                  isAddingCheckoutSession={isAddingCheckoutSession}
                  subscriptionBillingInterval={subscriptionBillingInterval}
                  subscriptionTypeID={subscriptionTypeID}
                  subscriptionTypesLength={subscriptionTypes?.length}
                  getSubscription={getSubscription}
                  isFirstCard={index === 0}
                  subscriptionCreditOptionsID={subscriptionCreditOptionsID}
                />
              );
            })}
          </Grid>
        </Grid>
        <Grid
          item
          display='flex'
          gap={1}
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={'center'}
        >
          <Typography variant='h5' sx={{ color: '#8888' }}>
            Need a Custom Plan?
          </Typography>
          <Typography variant='h5'>
            <a
              href='mailto:support@sparkup.ai?subject=Custom%20SparkUp%20plan%20enquiry'
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              Contact Sales
            </a>
          </Typography>
        </Grid>
      </Grid>
    </SparkDialog>
  );
}

export default SubscriptionModal;
